// Склад (УТ) — первый раздел CRM поверх кеширующего прокси к 1С.
//
// ТОЛЬКО ЧТЕНИЕ, и это свойство конструкции, а не незаконченность: у прокси в ядре нет
// записи вовсе (см. core_api.py, секция «кеширующий прокси к 1С»). Оприходование,
// перемещение и резервирование остаются в 1С и на порталах.
//
// Сценарий взят из жизни, а не придуман: в peleng-portal самый частый запрос к складу —
// «есть ли у нас вот это» по куску названия или артикулу, с ответом «сколько всего,
// сколько в резерве, сколько свободно и на каком складе лежит». Всё остальное
// (группы, аналоги, история) — потом, по мере надобности.
import { useEffect, useMemo, useState } from 'react'

import { MIN_QUERY, useStockSearch } from '../api/stock'
import { FilterBar } from '../components/FilterBar'
import { OdataFreshness, OdataState } from '../components/OdataState'
import { fmtText, pluralN } from '../lib/format'
import { fmtQty } from '../lib/stock'
import type { StockItem } from '../lib/stock'

/** Пауза после последней буквы. Каждый запрос — это поход в 1С, посимвольно туда нельзя. */
const DEBOUNCE_MS = 400

function useDebounced(value: string, ms: number): string {
  const [out, setOut] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setOut(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return out
}

/**
 * Свободно = всего − резерв. Ноль и «не спрашивали» — разные вещи, поэтому не 0, а «—».
 *
 * Подпись колонки компонент получает снаружи: ячейку он рисует сам, а на телефоне таблица
 * разбирается на карточки, и подпись там берётся из самой ячейки — шапки на экране уже нет.
 */
function QtyCell({ v, label, accent }: { v: number | null; label: string; accent?: boolean }) {
  if (v === null) {
    return (
      <td className="num muted" data-label={label}
          title="остаток для этой позиции не запрашивался">—</td>
    )
  }
  return (
    <td className="num" data-label={label} style={accent && v > 0 ? { fontWeight: 600 } : undefined}>
      {v ? fmtQty(v) : <span className="muted">0</span>}
    </td>
  )
}

function StockDetails({ item }: { item: StockItem }) {
  return (
    <div className="card">
      <h2>{item.name || 'Позиция'}</h2>

      <dl className="dl">
        <dt>Артикул</dt>
        <dd className="mono">{fmtText(item.art)}</dd>
        <dt>Производитель</dt>
        <dd>{fmtText(item.producer)}</dd>
        <dt>Качество</dt>
        <dd>
          {item.quality
            ? <span className={`badge ${item.used ? 'lose' : 'won'}`}>{item.quality}</span>
            : <span className="muted">—</span>}
        </dd>
        {item.shelfDays > 0 && (
          <>
            <dt>Срок годности</dt>
            <dd>{item.shelfDays} дн.</dd>
          </>
        )}
      </dl>

      {item.have === null ? (
        <div className="hint" style={{ marginTop: 12 }}>
          Остаток по этой позиции не запрашивался — она за пределами первых позиций выдачи.
          Уточните запрос, чтобы она попала в начало списка.
        </div>
      ) : (
        <>
          <div className="tiles" style={{ marginTop: 12 }}>
            <div className="tile">
              <div className="t-label">Всего</div>
              <div className="t-sum">{fmtQty(item.have)}</div>
            </div>
            <div className="tile">
              <div className="t-label">В резерве</div>
              <div className="t-sum">{fmtQty(item.reserve)}</div>
            </div>
            <div className={`tile${item.free && item.free > 0 ? '' : ' hot'}`}>
              <div className="t-label">Свободно</div>
              <div className="t-sum">{fmtQty(item.free)}</div>
            </div>
          </div>

          <h2 style={{ marginTop: 16 }}>По складам</h2>
          {item.byWarehouse.length === 0 ? (
            <div className="empty"><div className="empty-title">Ни на одном складе остатка нет.</div></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Склад</th>
                  <th className="num">Остаток</th>
                </tr>
              </thead>
              <tbody>
                {item.byWarehouse.map((w) => (
                  <tr key={w.name}>
                    <td>{w.name}</td>
                    <td className="num">{fmtQty(w.qty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {/* Резерв 1С отдаёт суммой по позиции, а не по складам: разложить его
              по строкам выше нельзя, и придумывать разбивку мы не будем. */}
          {item.reserve ? (
            <div className="hint" style={{ marginTop: 8 }}>
              Резерв 1С показывает общей суммой по позиции — по складам он не разложен.
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}

export default function Stock() {
  const [input, setInput] = useState('')
  const q = useDebounced(input, DEBOUNCE_MS)
  const [selected, setSelected] = useState<string | null>(null)

  const search = useStockSearch(q)
  const items = useMemo(() => search.data?.items ?? [], [search.data])

  // Выбранная позиция пропала из новой выдачи — снимаем выбор, иначе справа висела бы
  // карточка товара, которого в списке уже нет.
  useEffect(() => {
    if (selected && !items.some((i) => i.ref === selected)) setSelected(null)
  }, [items, selected])

  const current = items.find((i) => i.ref === selected) ?? null
  const short = q.trim().length < MIN_QUERY

  return (
    <>
      <div className="page-head">
        <h1>Склад</h1>
        <div className="spacer" />
        <span className="muted">1С «Управление торговлей», только чтение</span>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="stock">
          <div className="f" style={{ flex: 1, minWidth: 260 }}>
            <label htmlFor="stock-q">Название или артикул</label>
            <input
              id="stock-q" value={input} autoFocus
              onChange={(e) => setInput(e.target.value)}
              placeholder={`минимум ${MIN_QUERY} символа, например «кабель 25»`}
            />
          </div>
        </FilterBar>
        <div className="hint" style={{ marginTop: 10 }}>
          Ищем по названию и по артикулу: каждое слово запроса должно найтись хотя бы
          в одном из них. Раскладку клавиатуры исправляем сами, если по набранному
          ничего не нашлось.
        </div>
      </div>

      {short ? (
        <div className="card muted">
          Введите минимум {MIN_QUERY} символа. Каждый поиск — это запрос в 1С,
          поэтому по одной букве мы её не дёргаем.
        </div>
      ) : (
        <OdataState isLoading={search.isLoading} error={search.error}>
          <div style={{ marginBottom: 12 }}>
            <OdataFreshness
              meta={search.data?.meta}
              fetching={search.isFetching}
              onRefresh={() => void search.refetch()}
            />
          </div>

          {search.data?.note && (
            <div className="notice" style={{ marginBottom: 12 }}>{search.data.note}</div>
          )}

          <div className="grid-2">
            <div className="card">
              <div className="page-head" style={{ marginBottom: 10 }}>
                <h2 style={{ margin: 0 }}>Найдено</h2>
                <div className="spacer" />
                <span className="hint">
                  {search.data
                    ? pluralN(search.data.found, 'позиция', 'позиции', 'позиций') : ''}
                  {search.isFetching ? ' · обновляю…' : ''}
                </span>
              </div>

              {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */}
              <div className="table-wrap cards">
                <table>
                  <thead>
                    <tr>
                      <th>Номенклатура</th>
                      <th className="nowrap">Артикул</th>
                      <th className="num">Всего</th>
                      <th className="num">Резерв</th>
                      <th className="num">Свободно</th>
                      <th className="nowrap">Склады</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((i) => (
                      <tr
                        key={i.ref}
                        onClick={() => setSelected(i.ref)}
                        style={{ cursor: 'pointer', background: i.ref === selected ? 'var(--accent-soft)' : undefined }}
                      >
                        <td data-label="Номенклатура">
                          {fmtText(i.name)}
                          {i.used && (
                            <span className="badge lose" style={{ marginLeft: 6 }}>{i.quality}</span>
                          )}
                        </td>
                        <td className="mono nowrap" data-label="Артикул">
                          {i.art || <span className="muted">—</span>}
                        </td>
                        <QtyCell v={i.have} label="Всего" />
                        <QtyCell v={i.reserve} label="Резерв" />
                        <QtyCell v={i.free} label="Свободно" accent />
                        <td className="hint" data-label="Склады">
                          {i.byWarehouse.length
                            ? i.byWarehouse.map((w) => `${w.name}: ${fmtQty(w.qty)}`).join(', ')
                            : '—'}
                        </td>
                      </tr>
                    ))}
                    {items.length === 0 && (
                      <tr>
                        <td colSpan={6} className="empty">
                          <div className="empty-title">Ничего не найдено</div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              {current ? (
                <StockDetails item={current} />
              ) : (
                <div className="card muted">
                  Выберите позицию слева — здесь покажем остатки по складам,
                  резерв и свободное количество.
                </div>
              )}

              <div className="card">
                <h2>Почему здесь нельзя ничего изменить</h2>
                <p className="muted" style={{ marginTop: 0 }}>
                  Раздел ходит в 1С через прокси ядра, а у него нет записи вовсе: наверх
                  идёт единственная функция и только GET. Оприходование, перемещение
                  и резервирование остаются в 1С и на складских порталах.
                </p>
              </div>
            </div>
          </div>
        </OdataState>
      )}
    </>
  )
}
